<template>
  <div :ref="`tagsListContainer-${index}`" class="w-full max-w-full flex flex-nowrap items-center gap-1 min-w-0">
    <!-- Render the ad's list of tags -->
    <div class="flex items-center gap-1 min-w-0">
      <div v-for="(tag, index) in visibleTags" :key="tag.id" class="px-3 py-1 rounded-full"
      :style="{ 
        backgroundColor: tag.color ? colors[tag.color].fill : colors['gray'].fill,
        color: tag.color ? colors[tag.color].stroke : colors['gray'].stroke,
      }">
        <div class="truncate font-medium" :style="{maxWidth: '170px'}">
          {{ tag.name }}
        </div>
      </div>
    </div>
    <!-- Renders the "+x" hidden tags indicator -->
    <div v-if="hiddenTags.length > 0" class="relative px-2 py-1 rounded-full text-text-muted bg-neutral-50 font-medium"
    @mouseenter="hiddenIndicatorHovered = true" @mouseleave="hiddenIndicatorHovered = false">
      +{{ hiddenTags.length }}
      <div v-if="hiddenIndicatorHovered" @mouseenter="hiddenIndicatorHovered = false" class="hidden-tags-tooltip pt-1.5 z-50">
        <div class="px-1 py-2 rounded-lg bg-neutral-700 shadow-lg">
          <div v-for="tag in hiddenTags" :key="tag.id" 
          class="px-3 py-1 text-neutral-200 font-medium text-sm max-w-full whitespace-nowrap truncate">
            {{ tag.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import FirebaseAPI from '@/api/firebase'
  import TAG_COLORS from '../../../utils/TagColors'

  export default {
    name: 'TagsListDisplay',
    props: {
      tags: {
        type: Array,
        required: true
      },
      index: {
        type: Number,
        required: true
      }
    },
    data () {
      return {
        adTags: [],
        visibleTags: [],
        colors: TAG_COLORS,
        resizeObserver: null,
        resizeRaf: null,
        hiddenIndicatorHovered: false,
      }
    },
    computed: {
      hiddenTags () {
        return this.adTags.filter(tag => !this.visibleTags.includes(tag));
      }
    },
    watch: {
      tags: {
        handler () {
          this.fetchAdTags();
        },
        immediate: true
      }
    },
    mounted () {
      this.$nextTick(() => {
        this.resizeObserver = new ResizeObserver(this.calculateVisibleTags)
        window.addEventListener('resize', this.calculateVisibleTags)
        this.resizeObserver.observe(this.$refs[`tagsListContainer-${this.index}`])
      })
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.calculateVisibleTags)
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf)
    },
    methods: {
      async fetchAdTags () {
        if (this.tags.length) {
          this.adTags = await FirebaseAPI.Tags.getTagsArray(this.tags);
          this.$nextTick(() => {
            this.calculateVisibleTags();
          })
        }
      },
      calculateVisibleTags () {
        cancelAnimationFrame(this.resizeRaf)
        this.resizeRaf = requestAnimationFrame(() => {
          if (!this.$refs[`tagsListContainer-${this.index}`]) return
          const containerWidth = this.$refs[`tagsListContainer-${this.index}`].offsetWidth - 40; // -30 for "+x" tag
          let totalWidth = 0;
          let newVisibleTags = [];

          // To calculate the expected width of the tag, we render a temporary element and capture its width
          this.adTags.forEach(tag => {
            const compNode = document.createElement('span');
            compNode.classList.add('px-4'); // Apply the same styles as the tag element (+1 x-padding for flex gap)
            compNode.textContent = tag.name;
            document.body.appendChild(compNode);
            const tagWidth = compNode.offsetWidth; // Compute the rendered width of the tag
            document.body.removeChild(compNode);

            if (totalWidth + tagWidth < containerWidth) {
              totalWidth += tagWidth;
              newVisibleTags.push(tag);
            }

            this.visibleTags = newVisibleTags;
          });
        })
      }
    }
  }
</script>

<style scoped>
  .hidden-tags-tooltip {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    min-width: 125px;
    max-width: 200px;
  }
  .v-enter-active, .v-leave-active {
    transition: opacity 150ms ease-in-out;
  }
  .v-enter-from, .v-enter, .v-leave-to {
    opacity: 0;
  }
  .v-enter-to, .v-leave-from {
    opacity: 1;
  }
</style>